import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, FolderOpen, Tv, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#6366f1', '#ef4444', '#10b981', '#f59e0b', '#3b82f6', '#ec4899', '#4A9EFF', '#8b5cf6'];

const SERVICE_META = {
  netflix: { name: 'Netflix', color: '#E50914' },
  hulu: { name: 'Hulu', color: '#1CE783' },
  disney_plus: { name: 'Disney+', color: '#113CCF' },
  hbo_max: { name: 'HBO Max', color: '#B535F6' },
  amazon_prime: { name: 'Amazon Prime', color: '#00A8E1' },
  apple_tv: { name: 'Apple TV+', color: '#555' },
  peacock: { name: 'Peacock', color: '#FDB927' },
  paramount_plus: { name: 'Paramount+', color: '#0064FF' },
};

export default function Folders() {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [shows, setShows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editFolder, setEditFolder] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#6366f1' });
  const [expandedAdds, setExpandedAdds] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [foldersRes, showsRes] = await Promise.all([
        axios.get(`${API}/folders`, { headers }),
        axios.get(`${API}/shows`, { headers }),
      ]);
      setFolders(foldersRes.data);
      setShows(showsRes.data);
    } catch (err) {
      console.error(err);
    }
  }, [getToken]);

  useEffect(() => { if (user) fetchData(); }, [fetchData, user]);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('Folder name is required');
      return;
    }
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      if (editFolder) {
        await axios.put(`${API}/folders/${editFolder.folder_id}`, form, { headers });
        toast.success('Folder updated');
      } else {
        await axios.post(`${API}/folders`, form, { headers });
        toast.success('Folder created!');
      }
      setForm({ name: '', color: '#6366f1' });
      setEditFolder(null);
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save folder');
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      const token = await getToken();
      await axios.delete(`${API}/folders/${folderId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Folder deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete folder');
    }
  };

  const assignShow = async (showId, folderId) => {
    try {
      const token = await getToken();
      await axios.put(`${API}/shows/${showId}`, { folder_id: folderId }, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      toast.error('Failed to assign show');
    }
  };

  const removeFromFolder = async (showId) => {
    try {
      const token = await getToken();
      await axios.put(`${API}/shows/${showId}`, { folder_id: '' }, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      toast.error('Failed to remove show');
    }
  };

  const unassignedShows = shows.filter(s => !s.folder_id);

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="folders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>Genre Folders</h1>
          <p className="text-neutral-500 mt-1">Organize your shows into custom folders</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) { setEditFolder(null); setForm({ name: '', color: '#6366f1' }); }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold gap-2 hover:opacity-90" data-testid="create-folder-btn">
              <Plus className="w-4 h-4" /> New Folder
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-bold" style={{ fontFamily: 'Outfit' }}>{editFolder ? 'Edit Folder' : 'Create Folder'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                placeholder="Folder name (e.g., Sci-Fi, Comedy)"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12"
                data-testid="folder-name-input"
              />
              <div>
                <p className="text-sm text-neutral-400 mb-2">Color</p>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-8 h-8 rounded-full transition-all ${form.color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0A0A0A] scale-110' : 'hover:scale-105'}`}
                      style={{ backgroundColor: c }}
                      data-testid={`color-${c}`}
                    />
                  ))}
                </div>
              </div>
              <Button onClick={handleSubmit} className="w-full bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold hover:opacity-90" data-testid="submit-folder-btn">
                {editFolder ? 'Save Changes' : 'Create Folder'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {folders.length === 0 ? (
        <div className="text-center py-16">
          <FolderOpen className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
          <p className="text-neutral-500">No folders yet. Create one to organize your shows!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {folders.map(folder => {
            const folderShows = shows.filter(s => s.folder_id === folder.folder_id);
            const coverArt = folderShows.find(s => s.image_url)?.image_url;
            return (
              <div key={folder.folder_id} className="rounded-2xl bg-[#0A0A0A] border border-white/5 overflow-hidden" data-testid={`folder-${folder.folder_id}`}>
                {/* Folder header with cover art */}
                <div className="relative h-16 overflow-hidden">
                  {coverArt ? (
                    <>
                      <div className="absolute inset-0 flex">
                        {folderShows.filter(s => s.image_url).slice(0, 4).map((s, i) => (
                          <div key={s.show_id} className="flex-1 overflow-hidden">
                            <img src={s.image_url} alt="" className="w-full h-16 object-cover object-top" style={{ filter: 'brightness(0.5)' }} />
                          </div>
                        ))}
                      </div>
                      <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${folder.color}40, transparent)` }} />
                    </>
                  ) : (
                    <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${folder.color}20, transparent)` }} />
                  )}
                  <div className="absolute inset-0 flex items-center justify-between px-5">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full shadow-lg" style={{ backgroundColor: folder.color }} />
                      <h3 className="font-bold text-white drop-shadow" style={{ fontFamily: 'Outfit' }}>{folder.name}</h3>
                      <span className="text-xs text-white/60">{folderShows.length} show{folderShows.length !== 1 ? 's' : ''}</span>
                    </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { setEditFolder(folder); setForm({ name: folder.name, color: folder.color }); setDialogOpen(true); }}
                      className="p-1.5 text-white/50 hover:text-white transition-colors"
                      data-testid={`edit-folder-${folder.folder_id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => deleteFolder(folder.folder_id)}
                      className="p-1.5 text-white/50 hover:text-red-400 transition-colors"
                      data-testid={`delete-folder-${folder.folder_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                </div>{/* end relative h-16 header */}

                <div className="p-5">
                  {folderShows.length === 0 ? (
                    <p className="text-sm text-neutral-600 py-4 text-center">No shows in this folder</p>
                  ) : (
                    <div className="space-y-2">
                      {folderShows.map(show => (
                        <div key={show.show_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.03] transition-colors group">
                          <div
                            className="w-10 h-14 rounded-lg bg-white/5 flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer"
                            onClick={() => show.tmdb_id && navigate(`/show/${show.tmdb_id}`, { state: { media_type: show.media_type || 'tv' } })}
                          >
                            {show.image_url ? <img src={show.image_url} alt={show.title} className="w-full h-full object-cover" /> : <Tv className="w-4 h-4 text-neutral-600" />}
                          </div>
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => show.tmdb_id && navigate(`/show/${show.tmdb_id}`, { state: { media_type: show.media_type || 'tv' } })}
                          >
                            <p className="text-sm truncate font-medium hover:text-violet-400 transition-colors">{show.title}</p>
                            <p className="text-xs text-neutral-600">{SERVICE_META[show.service_id]?.name}</p>
                          </div>
                          <button onClick={() => removeFromFolder(show.show_id)} className="p-1 text-neutral-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100" data-testid={`remove-from-folder-${show.show_id}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {unassignedShows.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <p className="text-xs text-neutral-500 mb-2">Add shows to this folder:</p>
                      <div className="flex flex-wrap gap-2">
                        {(expandedAdds[folder.folder_id] ? unassignedShows : unassignedShows.slice(0, 5)).map(show => (
                          <button
                            key={show.show_id}
                            onClick={() => assignShow(show.show_id, folder.folder_id)}
                            className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-neutral-400 hover:text-white hover:border-violet-500/30 transition-all"
                            data-testid={`assign-${show.show_id}-to-${folder.folder_id}`}
                          >
                            + {show.title}
                          </button>
                        ))}
                        {unassignedShows.length > 5 && (
                          <button
                            onClick={() => setExpandedAdds(prev => ({ ...prev, [folder.folder_id]: !prev[folder.folder_id] }))}
                            className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-neutral-500 hover:text-white hover:border-white/20 transition-all flex items-center gap-1"
                          >
                            {expandedAdds[folder.folder_id]
                              ? <><ChevronUp className="w-3 h-3" /> Show less</>
                              : <><ChevronDown className="w-3 h-3" /> +{unassignedShows.length - 5} more</>
                            }
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>{/* end p-5 content area */}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
